import { CONSULT_INTENT_KINDS, isConsultIntentKind, type ConsultIntentKind } from "./consultIntent";
import { SHEPHERD_STAGES, URGENCY_SIGNALS, type LeadAssessment, type LeadRecord, type LeadStage, type UrgencySignal } from "./leadQuality";
import type { Scenario } from "./scenario";
import type { InquiryType } from "./types";

/**
 * Loop item QUAL1: design queue rows Q3, Q4 and Q5
 * (docs/DESIGN-learn-qualify-2026-10-03.md, part A, filter the time-wasters),
 * the offline-now form. Kabir typed the ask on 2026-10-03: "One thing I do
 * need this system to do, is get rid of the time-wasters and filter as we
 * know."
 *
 * `qualifyLead` bands a lead `priority`, `standard`, `low_signal` or `unknown`
 * from codes only (Agent Brains 5.3: "score + route; nobody rejected,
 * everybody routed"). It is pure and deterministic, so every inbound message
 * can re-score the lead, and it is RECORD ONLY: `effect` is always "none" and
 * nothing reads the band. No call list, CLI, sweep, sender or prompt is wired
 * to it; Q15 does that later, behind its own flag.
 *
 * Built on the call list's own readers (`leadQuality.ts`), never a second
 * copy of them: the urgency signals, BANT need and timeline as `assessLead`
 * reports them, the routed scenario as its value key, and the
 * `consult.intent` kinds through the shared kind check. Budget and Authority
 * are never inputs (decision list item 148; the design's part A), and no VIP
 * field or wealth inference is a feature (F-11): VIP status only blocks the
 * later lane, it never moves the band.
 *
 * Decisions D26 to D41 are NOT ruled. Every number and list below that a
 * ruling may change carries a one-line marker, and the shipped lowering
 * catalogue is EMPTY, so the low band is unreachable in production: the
 * scorer only lifts leads until Kabir ratifies a catalogue (T2, seeded at the
 * interview, D36).
 *
 * Fix round 1 (two adversarial reviews): a lowering code is read against a
 * closed vocabulary (deny by default), so a natural name for a never-lower
 * trait, or for the absence of a raising signal, is refused rather than let
 * through by a denylist; a lead at a stage that blocks lowering is held; every
 * exported table is deep-frozen; and the options are validated like the
 * signals.
 *
 * Fix round 2 (one adversarial review): that vocabulary was one list shared by
 * the three namespaces, so the same words filed under each made one fact count
 * as two or three families, and a reply that never came, spelled "no answer",
 * passed as a non-timing entry or owner code. Each family now reads its own
 * word list, the lists share no word, and "answer" is reply timing; the
 * high-stakes rule is a table the version hash sees; and the shipped
 * catalogue's id is reserved for the empty catalogue.
 *
 * Fix round 3 (one adversarial review): stakes were read off a list of HIGH
 * routing keys, so every other key read standard, and the router sends a
 * discount or haggle word to `price_challenge` and a "price of" or "how much
 * is" to `ready_made_purchase` ahead of `custom_engagement` or `unknown`. A
 * buyer's price question dropped the lowering bar from three families to two.
 * The reader is deny by default now: every routing key reads high unless it is
 * on `STANDARD_STAKES_VALUE_KEYS`, which no key a price, discount, budget or
 * haggle phrase can reach may join, and which is empty.
 */

/** Freezes a table and everything in it, so no caller can edit a rule at runtime (fix round 1). */
function deepFreeze<V>(value: V): V {
  if (value !== null && typeof value === "object") {
    for (const inner of Object.values(value)) deepFreeze(inner);
    Object.freeze(value);
  }
  return value;
}

/** Bumped whenever a weight, a threshold, a list or a rule below changes, so a stored band names the rules that made it. */
export const QUALIFY_SCORER_VERSION = "qualify-0.4.0";

export type QualifyBand = "priority" | "standard" | "low_signal" | "unknown";

/** How the band is handled once a later item wires it: `unknown` is handled as `standard` (Agent Brains "unknown ≠ assumed"). */
export type QualifyHandling = "priority" | "standard" | "low_signal";

/**
 * The entry kinds a thread can open with. Mirrors `IngestKind` in
 * `packages/pipeline/src/webhook/parse.ts`; stored as a kind, never a story id
 * (design part A). Nothing in `LeadRecord` carries it today, so the caller
 * supplies it (Q15 wires the read).
 */
export const ENTRY_KINDS = deepFreeze(["message", "story_reply", "comment"] as const);
export type EntryKind = (typeof ENTRY_KINDS)[number];

/** A high-stakes lead meets a stricter lowering bar. Every lead is high stakes unless its routing is on `STANDARD_STAKES_VALUE_KEYS` (fix round 3). */
export type QualifyStakes = "standard" | "high";

/**
 * The codes that can only raise a lead, from the design's list: the
 * `consult.intent` kinds (plus `intent.kind_not_read` for a kind this build
 * cannot read, which raises and is never echoed), a story reply as entry
 * source (Composer Spec "story replies are the hottest intent signal"), the
 * call list's urgency signals, and Need and Timeline evidence. Positive
 * specificity codes (Q21) have no reader yet, so none is listed.
 */
export const RAISING_CODES = deepFreeze([
  "intent.consult",
  "intent.visit",
  "intent.call",
  "intent.kind_not_read",
  "entry.story_reply",
  "urgency.window_open",
  "urgency.window_grace",
  "urgency.sla_breached",
  "urgency.occasion_within_30d",
  "need.evidenced",
  "timeline.evidenced",
] as const);
export type RaisingCode = (typeof RAISING_CODES)[number];

/**
 * Each raising code's weight; the score is the sum over the codes present,
 * each once. Intent and urgency weigh most (Planning elements: "Intent/urgency
 * (biggest predictor)"), so every intent, story reply and urgency weight is
 * above every Need and Timeline weight, and a test pins that order.
 * AWAITING RATIFICATION (design D27, D30, docs/DESIGN-learn-qualify-2026-10-03.md): 3 intent and story reply, 2 urgency, 1 need and timeline.
 */
export const QUALIFY_RAISING_WEIGHTS: Readonly<Record<RaisingCode, number>> = deepFreeze({
  "intent.consult": 3,
  "intent.visit": 3,
  "intent.call": 3,
  "intent.kind_not_read": 3,
  "entry.story_reply": 3,
  "urgency.window_open": 2,
  "urgency.window_grace": 2,
  "urgency.sla_breached": 2,
  "urgency.occasion_within_30d": 2,
  "need.evidenced": 1,
  "timeline.evidenced": 1,
});

/**
 * A score at or over this bands `priority`. The shop's own clocks and Need
 * alone reach it only in the final 2h of a window with the SLA breached; any
 * customer intent plus one urgency signal and Need reaches it.
 * AWAITING RATIFICATION (design D27, D30, docs/DESIGN-learn-qualify-2026-10-03.md): priority at a score of 6.
 */
export const QUALIFY_PRIORITY_THRESHOLD = 6;

/**
 * The families a lowering code can come from. The intent family only raises,
 * so a catalogue entry in it is refused.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): engagement, entry source and owner history.
 */
export const LOWERING_FAMILIES = deepFreeze(["engagement", "entry_source", "owner_history"] as const);
export type LoweringFamily = (typeof LOWERING_FAMILIES)[number];

/**
 * The low band needs counted codes from at least this many DIFFERENT
 * families. Two codes from one family never lower a lead.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): 2 families.
 */
export const LOW_SIGNAL_MIN_FAMILIES = 2;

/**
 * The stricter bar for high-stakes leads (Auto-Learn Spec: "Thresholds scale
 * with stakes (repair ack ≠ $10K custom lead)"): every family must speak.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): 3 families for every high-stakes lead, which is every routing key off STANDARD_STAKES_VALUE_KEYS plus what HIGH_STAKES_RULES adds.
 */
export const LOW_SIGNAL_MIN_FAMILIES_HIGH_STAKES = 3;

/**
 * The routing keys (the call list's value key, `assessLead`'s latest routing)
 * that read as STANDARD stakes and so meet the two-family bar. Deny by default
 * (fix round 3): every key off this list reads high, `unrouted`, `unknown`, a
 * key this build does not name and a row the router gains later included.
 *
 * No key a price, discount, budget or haggle phrase can route to may join it:
 * a buyer's price question must never make a lead easier to lower than the
 * same message without it (Kabir, typed 2026-10-03: wealthy, serious buyers,
 * and a terse or price-first message from one is common). A test drives the
 * router with such phrases and holds that. Every inbound row is reachable that
 * way: discount and haggle words route to `price_challenge`, "price of" and
 * "how much is" to `ready_made_purchase`, "cost of resizing" to
 * `repair_inquiry`, "the cost of a consultation" to `consult_confirm`, "how
 * much is a lab grown 2ct" to `educated_buyer_debate`, and a budget word moves
 * no row at all, so it rides on whichever row the rest of the message picks.
 * The two outbound rows (`ghost_revival`, `post_purchase_care`) are never a
 * reading of a customer's message, so nothing says what their stakes are. So
 * the list is empty: every lead meets the three-family bar until D27 is ruled,
 * which only makes lowering harder. A key added here is a reviewed code change
 * that bumps `QUALIFY_SCORER_VERSION`.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the standard-stakes routing keys, empty, so every routing key reads high stakes, ready_made_purchase, price_challenge and educated_buyer_debate included.
 */
export const STANDARD_STAKES_VALUE_KEYS: readonly Scenario[] = deepFreeze([]);

/**
 * What reads as high stakes even on a routing key that
 * `STANDARD_STAKES_VALUE_KEYS` lists: a custom inquiry type on record, and an
 * engagement-ring routing on record. Every key off that list (all of them,
 * while it is empty) already reads high, `custom_engagement`, `unrouted` and
 * `unknown` among them. `readQualifySignals` reads stakes from these two
 * tables and nothing else, so the version hash sees the whole rule (fix rounds
 * 2 and 3).
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): every routing key off STANDARD_STAKES_VALUE_KEYS (custom_engagement, unrouted and unknown among them), a custom inquiry type and an engagement-ring routing read as high stakes.
 */
export const HIGH_STAKES_RULES: {
  readonly inquiryTypes: readonly InquiryType[];
  readonly engagementRoutingOnRecord: boolean;
} = deepFreeze({ inquiryTypes: ["custom"], engagementRoutingOnRecord: true });

/**
 * The raising codes that block lowering outright: asking to come in, to book
 * or to be called, a story reply, an occasion within 30 days and a timeline.
 * Intent and the story reply are the customer's own words. The occasion and
 * the timeline read wider than that: `leadQuality.ts` also reads into them
 * the occasion planner's proposed touches (from sale, deposit, appointment
 * and key-date rows), and the timeline a booked or confirmed appointment, so
 * a lead the shop has a date for is held as well, the safe direction. The
 * shop's own clocks (window open, grace, SLA breached) and Need raise but do
 * not block, since nearly every open lead carries one and a chatty
 * time-waster is always inside the window. A lead that scores `priority` is
 * never lowered either.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): intent, story reply, occasion within 30 days and timeline block lowering.
 */
export const LOWERING_BLOCKED_BY: readonly RaisingCode[] = deepFreeze([
  "intent.consult",
  "intent.visit",
  "intent.call",
  "intent.kind_not_read",
  "entry.story_reply",
  "urgency.occasion_within_30d",
  "timeline.evidenced",
]);

/**
 * The stages at which a lead is never lowered, whatever the families say: a
 * consult booked or attended, a deposit on record, Delivered, and a stage the
 * record cannot place (a past buyer, a lost or ghosted lead who wrote again,
 * a lead with no customer message), which is held rather than guessed. New,
 * Engaged and the Reactivation pool can still be lowered. The stage is the
 * call list's own (`assessLead`); it only blocks, so it never raises a lead,
 * never moves the score, and is checked after the raising codes.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): consult booked, deposit, delivered and not derivable block lowering.
 */
export const LOWERING_BLOCKED_BY_STAGES: readonly LeadStage[] = deepFreeze(["consult_booked", "deposit", "delivered", "not_derivable"]);

/** Every stage the call list can report, the Shepherd's six and not derivable. */
const LEAD_STAGES: readonly LeadStage[] = [...SHEPHERD_STAGES, "not_derivable"];

/**
 * The design's never-lower list, in its order (design part A).
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the design's never-lower classes.
 */
export const NEVER_LOWER_CLASSES = deepFreeze([
  "price_asked_first",
  "discount_word",
  "price_challenge",
  "message_length",
  "spelling",
  "language",
  "register",
  "tone",
  "budget",
  "authority",
  "vip",
  "wealth_inference",
] as const);
export type NeverLowerClass = (typeof NEVER_LOWER_CLASSES)[number];

/**
 * How a code is read into a never-lower class: a code is split on dots and
 * underscores and a token matches whole, or a stem matches anywhere in the
 * code. The first class in the list that matches is the one named. A lexical
 * reader over-refuses on purpose: a legitimate code refused loudly is renamed
 * by a human, a never-lower trait let through lowers a buyer silently. It is
 * the named refusal, not the guard: a name it misses is still refused by
 * `LOWERING_CODE_VOCABULARY`, which lets through only the words on its list.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the tokens and stems that read a code as a never-lower class.
 */
export const NEVER_LOWER_MATCHERS: Readonly<Record<NeverLowerClass, { readonly tokens: readonly string[]; readonly stems: readonly string[] }>> = deepFreeze({
  price_asked_first: { tokens: ["cost", "costs", "quote", "quotes", "quoted", "msrp", "amount", "ballpark", "much"], stems: ["pric"] },
  discount_word: {
    tokens: ["deal", "deals", "sale", "sales", "offer", "offers", "cheap", "cheaper", "cheapest", "markdown", "clearance"],
    stems: ["discount", "promo", "coupon", "voucher", "rebate", "percent", "pct", "bogo", "freebie"],
  },
  price_challenge: {
    tokens: ["challenge", "challenged", "objection", "objections", "counter", "counteroffer"],
    stems: ["haggl", "negotiat", "bargain", "lowball"],
  },
  message_length: {
    tokens: ["terse", "short", "shorter", "long", "longer", "brief", "word", "words", "line", "lines", "char", "chars", "characters", "oneliner", "verbose", "monosyllabic"],
    stems: ["length"],
  },
  spelling: { tokens: ["caps", "lowercase", "uppercase"], stems: ["spell", "typo", "grammar", "grammat", "punctuat", "capitali"] },
  language: { tokens: ["lang", "foreign", "accent", "esl", "dialect", "native"], stems: ["languag", "lingu", "translat", "english"] },
  register: { tokens: ["slang", "casual", "polite", "impolite", "register", "abbreviation", "abbreviations", "textspeak"], stems: ["formal", "colloquial"] },
  tone: {
    tokens: ["tone", "rude", "curt", "blunt", "attitude", "emotion", "emotional", "angry", "pushy", "demanding", "sentiment", "mood"],
    stems: ["emoj"],
  },
  budget: { tokens: ["spend", "spender", "spending", "frugal", "thrifty", "broke"], stems: ["budget", "afford"] },
  authority: {
    tokens: ["authority", "decision", "decider", "approver", "spouse", "partner", "wife", "husband", "fiance", "fiancee", "gift", "boss"],
    stems: [],
  },
  vip: { tokens: ["tier", "whale"], stems: ["vip"] },
  wealth_inference: {
    tokens: ["rich", "poor", "luxury", "zip", "zipcode", "postcode", "postal", "neighborhood", "neighbourhood", "follower", "followers", "verified", "lifestyle"],
    stems: ["wealth", "income", "salary", "networth", "affluen"],
  },
});

/**
 * A lowering code that names reply timing must be declared `timing: true`,
 * and timing lives in the engagement family only, where it counts once at
 * most. So reply delay can never lower a lead alone: it is one code in one
 * family, and the bar is two families. A reply that never came is timing too
 * ("no reply", "no answer", "went quiet", "ghosted"), and so is a duration
 * (`REPLY_TIMING_DURATION_SHAPE`), so neither can stand as a second family.
 * Only the engagement word list carries a timing word (fix round 2).
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the tokens that read a code as reply timing.
 */
export const REPLY_TIMING_TOKENS: readonly string[] = deepFreeze([
  "delay",
  "delayed",
  "slow",
  "slower",
  "late",
  "later",
  "latency",
  "timing",
  "wait",
  "waited",
  "waiting",
  "lag",
  "lagged",
  "response",
  "responded",
  "responds",
  "reply",
  "replies",
  "replied",
  "replying",
  "answer",
  "answers",
  "answered",
  "unanswered",
  "quiet",
  "silent",
  "silence",
  "ghost",
  "ghosted",
  "ghosting",
  "day",
  "days",
  "hour",
  "hours",
  "minute",
  "minutes",
  "week",
  "weeks",
]);

/**
 * A word shaped like a duration (3d, 12h, 2w, 30m) names reply timing as well.
 * Held as a string so the version hash reads it.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): a number then m, h, d or w reads as reply timing.
 */
export const REPLY_TIMING_DURATION_SHAPE = "^[0-9]+[mhdw]$";
const DURATION_WORD = new RegExp(REPLY_TIMING_DURATION_SHAPE);

/**
 * Each lowering family's namespace. A code's family is read off its name, and
 * a catalogue entry filed in another family is refused (fix round 1). The
 * namespace alone does not keep a fact to one family: the words after it are
 * read against that family's own list in `LOWERING_CODE_VOCABULARY`, and the
 * lists share no word, so the same words can never stand in two families (fix
 * round 2). Owner history is the owner's own mark only (`owner.marked_`, then
 * the owner history words), never a pattern read from the owner's past
 * dealings.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): engagement., entry. and owner.marked_.
 */
export const LOWERING_CODE_NAMESPACES: Readonly<Record<LoweringFamily, string>> = deepFreeze({
  engagement: "engagement.",
  entry_source: "entry.",
  owner_history: "owner.marked_",
});

/**
 * Words that name a raising signal. A lowering code that uses one is refused:
 * the absence of intent, a story reply, an occasion, a need or a timeline is
 * the unknown band's business (a lead with no raising code is unknown, handled
 * as standard), never a reason to lower.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the words that read a lowering code as the absence of a raising signal.
 */
export const RAISING_SIGNAL_TOKENS: readonly string[] = deepFreeze([
  "intent",
  "consult",
  "visit",
  "call",
  "book",
  "booking",
  "booked",
  "appointment",
  "story",
  "urgency",
  "urgent",
  "occasion",
  "need",
  "needs",
  "timeline",
  "date",
  "deposit",
]);

/**
 * The closed list of words each family's codes may use after the family's
 * namespace, read against that family's list only. A denylist of never-lower
 * words is lexical, and fix round 1 found natural names it let through; these
 * lists are deny-by-default instead: a word off its family's list is refused
 * by name, and adding one is a reviewed code change that bumps
 * `QUALIFY_SCORER_VERSION`. The lists share no word (fix round 2: one shared
 * list let the same words stand under all three namespaces, one fact counted
 * as three families), and only engagement carries a reply timing word. Seeded
 * from the synthetic test catalogue's words only; the catalogue Kabir rules on
 * at the interview (T2, D36) brings its own words, each read against the
 * never-lower list, the raising signals and reply timing, and kept to one
 * family.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the words a lowering code may use, a separate list per family.
 */
export const LOWERING_CODE_VOCABULARY: Readonly<Record<LoweringFamily, readonly string[]>> = deepFreeze({
  /**
   * The shop's answered turns and what came after them. Every code that uses
   * a reply or answer word, or a duration, must be declared timing.
   * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the engagement words.
   */
  engagement: ["no", "reply", "after", "answer", "answers", "two", "timing", "3d", "7d"],
  /**
   * How the thread arrived, as a kind of source (design part A: "a list you name").
   * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the entry source words.
   */
  entry_source: ["giveaway", "comment", "contest", "tag"],
  /**
   * After `owner.marked_`: the owner marked this contact as a time-waster
   * (design part A), and nothing else.
   * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): the owner history words.
   */
  owner_history: ["time", "waster"],
});

export interface LoweringCatalogueEntry {
  readonly code: string;
  readonly family: LoweringFamily;
  /** Declared reply timing: engagement only, counted once at most. */
  readonly timing?: true;
}

/** The codes Kabir ratifies as able to lower a lead (T2, design D27). */
export interface LoweringCatalogue {
  readonly id: string;
  readonly entries: readonly LoweringCatalogueEntry[];
}

/**
 * The shipped catalogue: EMPTY, so no observation is ever counted and the low
 * band is unreachable in production. Tests inject a synthetic one
 * (`qualifyLeadFixtures.ts`) to prove the rules a ratified one will meet.
 * AWAITING RATIFICATION (design D27, docs/DESIGN-learn-qualify-2026-10-03.md): empty until Kabir ratifies a catalogue, seeded at the interview.
 */
export const LOWERING_CATALOGUE: LoweringCatalogue = deepFreeze<LoweringCatalogue>({ id: "lowering_catalogue_unratified_empty", entries: [] });

/**
 * One lowering code observed on the lead. `shopAnsweredInWindow` matters to
 * the engagement family only: an engagement code counts only when it rests on
 * a turn the shop answered inside the window, and absent or null reads as
 * not answered.
 */
export interface LoweringObservation {
  readonly code: string;
  readonly shopAnsweredInWindow?: boolean | null;
}

/**
 * Everything the scorer reads, and nothing else: an exact key allowlist, so a
 * budget, an authority, a VIP field, a message length, a language or a
 * scenario cannot ride in. `stakes` is carried instead of the scenario, so the
 * scorer never reads a scenario name, but the scenario still decides the
 * stakes, and a price, discount, budget or haggle word moves the routing (to
 * `price_challenge` or `ready_made_purchase`, say). So the stakes reader is
 * deny by default: every routing key reads high unless it is on
 * `STANDARD_STAKES_VALUE_KEYS`, and no key such a word can reach is on it (fix
 * round 3). `stage` is the call list's stage and only blocks lowering
 * (`LOWERING_BLOCKED_BY_STAGES`).
 */
export interface QualifySignals {
  readonly urgency: readonly UrgencySignal[];
  readonly consultIntents: readonly (ConsultIntentKind | "kind_not_read")[];
  readonly entryKind: EntryKind | null;
  readonly need: boolean;
  readonly timeline: boolean;
  readonly stakes: QualifyStakes;
  readonly stage: LeadStage;
  readonly lowering: readonly LoweringObservation[];
}

export const QUALIFY_SIGNAL_KEYS: readonly (keyof QualifySignals)[] = deepFreeze([
  "urgency",
  "consultIntents",
  "entryKind",
  "need",
  "timeline",
  "stakes",
  "stage",
  "lowering",
]);

export interface QualifyOptions {
  /**
   * The contact's VIP status as `autonomy.ts` reads it: only exactly `false`
   * opens the lane. `true` is a VIP, and null or malformed is held as
   * possibly VIP. It never moves the band. The key is required: an options
   * object without it is refused, never read as unknown. A raw column value
   * is not a VIP status: the contacts table's VIP column is NOT NULL
   * DEFAULT false, so a contact nobody assessed reads false there. Whether
   * false with no VIP confidence computed reads as unknown is Kabir's
   * decision 1 under TA-216; until he rules, the caller (Q15) passes null for
   * it, the hold direction.
   */
  readonly contactVip: unknown;
  /** Defaults to the shipped (empty) catalogue. Validated before it is read. */
  readonly catalogue?: LoweringCatalogue;
}

/** Why the low band was not reached, in the order the checks run; null when it was. */
export type LoweringHold = "no_lowering_code" | "families_below_bar" | "blocked_by_raising" | "blocked_by_stage" | "priority_score";

export type NotCountedReason = "not_in_catalogue" | "engagement_not_answered_in_window" | "timing_counted_once";

export interface QualifyResult {
  readonly band: QualifyBand;
  readonly handledAs: QualifyHandling;
  readonly score: number;
  /** Sorted, each once. Codes only. */
  readonly raising: readonly RaisingCode[];
  /** The lowering codes counted, sorted. Codes only. */
  readonly lowering: readonly string[];
  /** The families of the counted codes, in `LOWERING_FAMILIES` order. */
  readonly loweringFamilies: readonly LoweringFamily[];
  readonly loweringHeld: LoweringHold | null;
  /** Observed codes that did not count, sorted by code, each with why. */
  readonly notCounted: readonly { readonly code: string; readonly why: NotCountedReason }[];
  /** True unless VIP is known false: the later lane may not apply. The band is reported either way. */
  readonly laneBlocked: boolean;
  readonly laneBlockedBy: "vip" | "vip_unknown" | null;
  /** Record only: nothing reads the band in this build. */
  readonly effect: "none";
  readonly scorerVersion: string;
  readonly catalogueId: string;
}

const CODE_SHAPE = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;
const CODE_MAX = 64;
const CATALOGUE_ID_SHAPE = /^[a-z][a-z0-9_]*$/;
const IDENTIFIER_SHAPE = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

const isCode = (v: unknown): v is string => typeof v === "string" && v.length <= CODE_MAX && CODE_SHAPE.test(v);
const tokensOf = (code: string): string[] => code.split(/[._]/);
const isRaisingCode = (code: string): code is RaisingCode => (RAISING_CODES as readonly string[]).includes(code);
const byString = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** The never-lower class a code falls in, or null. The first class in the design's order that matches is named. */
export function neverLowerClassOf(code: string): NeverLowerClass | null {
  const tokens = tokensOf(code);
  for (const klass of NEVER_LOWER_CLASSES) {
    const m = NEVER_LOWER_MATCHERS[klass];
    if (m.tokens.some((t) => tokens.includes(t)) || m.stems.some((s) => code.includes(s))) return klass;
  }
  return null;
}

const namesReplyTiming = (code: string): boolean => tokensOf(code).some((t) => REPLY_TIMING_TOKENS.includes(t) || DURATION_WORD.test(t));

/** The lowering family a code's namespace puts it in, or null when it sits in none. */
export function familyOfLoweringCode(code: string): LoweringFamily | null {
  return LOWERING_FAMILIES.find((f) => code.startsWith(LOWERING_CODE_NAMESPACES[f])) ?? null;
}

/**
 * Why a code-shaped value can never be a lowering code, or null when it can.
 * Shared by the catalogue and the observations, so neither carries what the
 * other refuses. In order: a raising code, a never-lower class, a code outside
 * the namespaces, a raising-signal word, reply timing outside engagement
 * (checked on its own, so a later word-list edit cannot move timing out of
 * engagement), a word off its own family's list. Every word it names is
 * code-shaped already, so naming it quotes no free text.
 */
function loweringCodeRefusal(code: string): string | null {
  if (isRaisingCode(code)) return `"${code}" is a raising code, and a raising code never lowers a lead; refused`;
  const klass = neverLowerClassOf(code);
  if (klass !== null) return `"${code}" falls in never-lower class ${klass} (design D27); refused`;
  const family = familyOfLoweringCode(code);
  if (family === null) {
    const spaces = LOWERING_FAMILIES.map((f) => LOWERING_CODE_NAMESPACES[f]).join(", ");
    return `"${code}" is outside the lowering namespaces (${spaces}); owner history is the owner's own mark only; refused`;
  }
  const words = code.slice(LOWERING_CODE_NAMESPACES[family].length).split(/[._]/);
  if (words.some((w) => w === "")) return `"${code}" has an empty word after its namespace; refused`;
  const raisingWord = words.find((w) => RAISING_SIGNAL_TOKENS.includes(w));
  if (raisingWord !== undefined) {
    return `"${code}" uses the raising signal word "${raisingWord}": a missing raising signal reads as unknown, never as a lowering code; refused`;
  }
  if (family !== "engagement" && namesReplyTiming(code)) {
    return `"${code}" names reply timing outside engagement; reply timing is declared timing: true and counts in the engagement family only, once; refused`;
  }
  const offList = words.find((w) => !LOWERING_CODE_VOCABULARY[family].includes(w));
  if (offList !== undefined) {
    return `"${code}" uses the word "${offList}", which is off the ${family} vocabulary (LOWERING_CODE_VOCABULARY); each family reads its own words only, and a new word needs a reviewed code change; refused`;
  }
  return null;
}

/**
 * Refuses, loudly, a catalogue that could lower a lead on anything the design
 * forbids: a value that is not a code (never quoted), a family off the list or
 * the intent family, a code `loweringCodeRefusal` refuses, a code filed in a
 * family other than its namespace's, reply timing undeclared or outside
 * engagement, a duplicate, or the shipped catalogue's reserved id on a
 * catalogue with entries (fix round 2: a lowered result would then name the
 * empty shipped catalogue as its source). Returns a deep-frozen copy when it
 * passes, so the caller's own array can never change what was checked.
 */
export function validateLoweringCatalogue(catalogue: LoweringCatalogue): LoweringCatalogue {
  const where = "lowering catalogue";
  if (catalogue === null || typeof catalogue !== "object" || !Array.isArray(catalogue.entries)) {
    throw new Error(`${where}: expected { id, entries: [] }; refused`);
  }
  if (typeof catalogue.id !== "string" || catalogue.id.length > CODE_MAX || !CATALOGUE_ID_SHAPE.test(catalogue.id)) {
    throw new Error(`${where}: the catalogue id is not a code (lowercase letters, digits and underscores); refused without quoting it`);
  }
  if (catalogue.id === LOWERING_CATALOGUE.id && catalogue.entries.length > 0) {
    throw new Error(`${where}: the id "${LOWERING_CATALOGUE.id}" is reserved for the shipped empty catalogue; a catalogue with entries needs its own id; refused`);
  }
  const seen = new Set<string>();
  const entries: LoweringCatalogueEntry[] = [];
  catalogue.entries.forEach((entry, i) => {
    if (entry === null || typeof entry !== "object") throw new Error(`${where} ${catalogue.id}: entry ${i} is not an object; refused`);
    const extra = Object.keys(entry).filter((k) => k !== "code" && k !== "family" && k !== "timing");
    if (extra.length > 0) throw new Error(`${where} ${catalogue.id}: entry ${i} carries ${extra.length} field(s) beyond code, family and timing; refused`);
    const { code, family, timing } = entry as { code: unknown; family: unknown; timing?: unknown };
    if (!isCode(code)) {
      throw new Error(`${where} ${catalogue.id}: entry ${i} is not a code (lowercase letters, digits, underscores and dots, at most ${CODE_MAX}); refused without quoting it`);
    }
    if (!(LOWERING_FAMILIES as readonly unknown[]).includes(family)) {
      const named = typeof family === "string" && IDENTIFIER_SHAPE.test(family) ? `family "${family}"` : "a family that is not a code";
      throw new Error(`${where} ${catalogue.id}: "${code}" names ${named} is not a lowering family (${LOWERING_FAMILIES.join(", ")}); the intent family only raises`);
    }
    const refusal = loweringCodeRefusal(code);
    if (refusal !== null) throw new Error(`${where} ${catalogue.id}: ${refusal}`);
    const named = familyOfLoweringCode(code);
    if (named === null || named !== family) {
      throw new Error(`${where} ${catalogue.id}: "${code}" is filed as ${String(family)}, but its namespace puts it in the ${String(named)} family; refused`);
    }
    if (timing !== undefined && timing !== true) throw new Error(`${where} ${catalogue.id}: "${code}" timing must be true or absent; refused`);
    if (timing === true && family !== "engagement") {
      throw new Error(`${where} ${catalogue.id}: "${code}" is declared timing, and reply timing counts in the engagement family only; refused`);
    }
    if (timing !== true && namesReplyTiming(code)) {
      throw new Error(`${where} ${catalogue.id}: "${code}" names reply timing, so it must be declared timing: true (engagement only, counted once); refused`);
    }
    if (seen.has(code)) throw new Error(`${where} ${catalogue.id}: duplicate code "${code}"; refused`);
    seen.add(code);
    entries.push(timing === true ? { code, family: named, timing: true } : { code, family: named });
  });
  return deepFreeze<LoweringCatalogue>({ id: catalogue.id, entries });
}

const listOf = <V>(v: unknown, allowed: readonly V[], key: string, what: string): void => {
  if (!Array.isArray(v) || v.some((x) => !(allowed as readonly unknown[]).includes(x))) {
    throw new Error(`qualifyLead: signal ${key} must be a list of ${what} (${allowed.join(", ")}); a value off that list is refused without quoting it`);
  }
};

/** The runtime half of the key allowlist: a missing key, an extra key or a value off its list throws. Never quotes a value it could not read. */
function validateSignals(signals: QualifySignals): void {
  if (signals === null || typeof signals !== "object") throw new Error("qualifyLead: signals must be an object; refused");
  for (const key of Object.keys(signals)) {
    if (!(QUALIFY_SIGNAL_KEYS as readonly string[]).includes(key)) {
      const named = IDENTIFIER_SHAPE.test(key) ? `"${key}"` : "that is not an identifier";
      throw new Error(`qualifyLead: unexpected signal key ${named}; the scorer reads ${QUALIFY_SIGNAL_KEYS.join(", ")} and nothing else (no budget, authority or VIP)`);
    }
  }
  for (const key of QUALIFY_SIGNAL_KEYS) {
    if (!(key in signals)) throw new Error(`qualifyLead: missing signal key "${key}"; refused rather than read as empty`);
  }
  listOf(signals.urgency, URGENCY_SIGNALS, "urgency", "urgency signals");
  listOf(signals.consultIntents, [...CONSULT_INTENT_KINDS, "kind_not_read"], "consultIntents", "consult.intent kinds");
  if (signals.entryKind !== null && !(ENTRY_KINDS as readonly unknown[]).includes(signals.entryKind)) {
    throw new Error(`qualifyLead: signal entryKind must be null or one of ${ENTRY_KINDS.join(", ")}; refused without quoting it`);
  }
  if (typeof signals.need !== "boolean") throw new Error("qualifyLead: signal need must be a boolean; refused");
  if (typeof signals.timeline !== "boolean") throw new Error("qualifyLead: signal timeline must be a boolean; refused");
  if (signals.stakes !== "standard" && signals.stakes !== "high") throw new Error('qualifyLead: signal stakes must be "standard" or "high"; refused');
  if (!(LEAD_STAGES as readonly unknown[]).includes(signals.stage)) {
    throw new Error(`qualifyLead: signal stage must be one of ${LEAD_STAGES.join(", ")}; refused without quoting it`);
  }
  if (!Array.isArray(signals.lowering)) throw new Error("qualifyLead: signal lowering must be a list; refused");
  signals.lowering.forEach((o, i) => {
    if (o === null || typeof o !== "object") throw new Error(`qualifyLead: lowering observation ${i} is not an object; refused`);
    const extra = Object.keys(o).filter((k) => k !== "code" && k !== "shopAnsweredInWindow");
    if (extra.length > 0) throw new Error(`qualifyLead: lowering observation ${i} carries ${extra.length} field(s) beyond code and shopAnsweredInWindow; refused`);
    if (!isCode(o.code)) throw new Error(`qualifyLead: lowering observation ${i} is not a code; refused without quoting it`);
    const answered = o.shopAnsweredInWindow;
    if (answered !== undefined && answered !== null && typeof answered !== "boolean") {
      throw new Error(`qualifyLead: lowering observation "${o.code}" shopAnsweredInWindow must be a boolean, null or absent; refused`);
    }
    const refusal = loweringCodeRefusal(o.code);
    if (refusal !== null) throw new Error(`qualifyLead: lowering observation ${refusal}`);
  });
}

const QUALIFY_OPTION_KEYS: readonly string[] = ["contactVip", "catalogue"];

/** The options get the signals' discipline: an object, contactVip present, nothing else. Never quotes a value. */
function validateOptions(options: QualifyOptions): void {
  if (options === null || typeof options !== "object" || Array.isArray(options)) throw new Error("qualifyLead: options must be an object with contactVip; refused");
  for (const key of Object.keys(options)) {
    if (!QUALIFY_OPTION_KEYS.includes(key)) {
      const named = IDENTIFIER_SHAPE.test(key) ? `"${key}"` : "that is not an identifier";
      throw new Error(`qualifyLead: unexpected option ${named}; the scorer takes ${QUALIFY_OPTION_KEYS.join(" and ")} only (no budget, authority or VIP field); refused`);
    }
  }
  if (!("contactVip" in options)) {
    throw new Error('qualifyLead: missing option "contactVip"; refused rather than read as unknown, so the VIP read is always a decision the caller made');
  }
}

function raisingCodesOf(signals: QualifySignals): RaisingCode[] {
  const codes = new Set<RaisingCode>();
  for (const kind of signals.consultIntents) codes.add(`intent.${kind}`);
  if (signals.entryKind === "story_reply") codes.add("entry.story_reply");
  for (const signal of signals.urgency) codes.add(`urgency.${signal}`);
  if (signals.need) codes.add("need.evidenced");
  if (signals.timeline) codes.add("timeline.evidenced");
  return [...codes].sort(byString);
}

/**
 * Bands one lead from codes. Pure: no clock, no I/O, no input mutated, and
 * the order lists arrive in never changes the result. Validates the options,
 * the signals and the catalogue first and throws on anything it cannot read.
 */
export function qualifyLead(signals: QualifySignals, options: QualifyOptions): QualifyResult {
  validateOptions(options);
  validateSignals(signals);
  const catalogue = validateLoweringCatalogue(options.catalogue ?? LOWERING_CATALOGUE);

  const raising = raisingCodesOf(signals);
  const score = raising.reduce((sum, code) => sum + QUALIFY_RAISING_WEIGHTS[code], 0);

  // One reading per observed code, in code order, so "timing counts once"
  // keeps the same code whatever order the observations arrive in. An
  // engagement code counts when any observation of it rests on a turn the
  // shop answered inside the window.
  const answeredByCode = new Map<string, boolean>();
  for (const o of signals.lowering) answeredByCode.set(o.code, (answeredByCode.get(o.code) ?? false) || o.shopAnsweredInWindow === true);
  const entryByCode = new Map(catalogue.entries.map((e) => [e.code, e] as const));
  const counted: LoweringCatalogueEntry[] = [];
  const notCounted: { code: string; why: NotCountedReason }[] = [];
  let timingCounted = false;
  for (const code of [...answeredByCode.keys()].sort(byString)) {
    const entry = entryByCode.get(code);
    if (entry === undefined) notCounted.push({ code, why: "not_in_catalogue" });
    else if (entry.family === "engagement" && answeredByCode.get(code) !== true) notCounted.push({ code, why: "engagement_not_answered_in_window" });
    else if (entry.timing === true && timingCounted) notCounted.push({ code, why: "timing_counted_once" });
    else {
      if (entry.timing === true) timingCounted = true;
      counted.push(entry);
    }
  }

  const loweringFamilies = LOWERING_FAMILIES.filter((f) => counted.some((e) => e.family === f));
  const bar = signals.stakes === "high" ? LOW_SIGNAL_MIN_FAMILIES_HIGH_STAKES : LOW_SIGNAL_MIN_FAMILIES;
  const priority = score >= QUALIFY_PRIORITY_THRESHOLD;
  const loweringHeld: LoweringHold | null =
    counted.length === 0
      ? "no_lowering_code"
      : loweringFamilies.length < bar
        ? "families_below_bar"
        : raising.some((c) => LOWERING_BLOCKED_BY.includes(c))
          ? "blocked_by_raising"
          : LOWERING_BLOCKED_BY_STAGES.includes(signals.stage)
            ? "blocked_by_stage"
            : priority
              ? "priority_score"
              : null;

  const band: QualifyBand = loweringHeld === null ? "low_signal" : raising.length === 0 ? "unknown" : priority ? "priority" : "standard";
  const vip = options.contactVip;
  const laneBlockedBy = vip === false ? null : vip === true ? "vip" : "vip_unknown";
  const laneBlocked = laneBlockedBy !== null;
  const handledAs: QualifyHandling = band === "unknown" || (band === "low_signal" && laneBlocked) ? "standard" : band;

  return {
    band,
    handledAs,
    score,
    raising,
    lowering: counted.map((e) => e.code),
    loweringFamilies,
    loweringHeld,
    notCounted,
    laneBlocked,
    laneBlockedBy,
    effect: "none",
    scorerVersion: QUALIFY_SCORER_VERSION,
    catalogueId: catalogue.id,
  };
}

export interface QualifyExtras {
  /** How the thread opened, as a kind; null when not known. */
  readonly entryKind: EntryKind | null;
  readonly lowering: readonly LoweringObservation[];
}

/**
 * The signals for one lead, read off the call list's own assessment so the
 * two can never disagree: urgency is `assessLead`'s signals, Need and
 * Timeline are its BANT components when `evidenced` (held or no evidence is
 * not), stakes are high unless its value key is on
 * `STANDARD_STAKES_VALUE_KEYS` (deny by default, fix round 3), and high on the
 * record's inquiry types and engagement-ring marker as `HIGH_STAKES_RULES`
 * reads them, and the stage is its stage. Reads no contact, memory, budget or
 * authority field. Entry kind and lowering observations have no reader in the
 * record yet, so the caller passes them (Q15 wires them).
 */
export function readQualifySignals(record: LeadRecord, assessment: LeadAssessment, extras: QualifyExtras): QualifySignals {
  if (record.threadId !== assessment.threadId) {
    throw new Error(`readQualifySignals: the record is thread ${record.threadId} and the assessment is thread ${assessment.threadId}; refused`);
  }
  // The value key is the LATEST routing only, so a custom thread whose last
  // turn was a price question reads that price question's row. While the
  // standard list is empty every row reads high and the latest is enough.
  // Carried to Q15, before any key joins the standard list: read stakes from
  // the highest-stakes routing on the thread, not the latest.
  const standardKey = (STANDARD_STAKES_VALUE_KEYS as readonly string[]).includes(assessment.value.key);
  const highStakes =
    !standardKey ||
    record.outcomes.some((o) => o.inquiryType !== null && HIGH_STAKES_RULES.inquiryTypes.includes(o.inquiryType)) ||
    (HIGH_STAKES_RULES.engagementRoutingOnRecord && (record.engagementRouting ?? null) !== null);
  const signals: QualifySignals = {
    urgency: assessment.urgency.signals.map((s) => s.signal),
    consultIntents: record.consultIntents.map((c) => (isConsultIntentKind(c.kind) ? c.kind : "kind_not_read")),
    entryKind: extras.entryKind,
    need: assessment.bant.need.status === "evidenced",
    timeline: assessment.bant.timeline.status === "evidenced",
    stakes: highStakes ? "high" : "standard",
    stage: assessment.stage,
    lowering: extras.lowering,
  };
  validateSignals(signals);
  return signals;
}
